"use client";

import { ArrowLeft, CreditCard, Loader2, PackageCheck, ShieldCheck, ShoppingCart, Truck, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Alert } from "@nitro/ui";
import { apiFetch } from "../lib/api";
import { itemRequiresShipping, maxQuantity, money, priceForVariant, primaryImage, productSummary, typeLabel } from "../lib/shop";
import { useShopCart } from "./ShopCartProvider";

export function ShopProductDetailClient({ initialDetail }) {
  const { addProduct, countryIso2 } = useShopCart();
  const [detail, setDetail] = useState(initialDetail);
  const [selectedVariantSlug, setSelectedVariantSlug] = useState(initialDetail?.variants?.[0]?.slug || "");
  const [quantity, setQuantity] = useState(1);
  const [state, setState] = useState({ loading: false, error: "" });

  const product = detail?.product || initialDetail?.product || null;
  const variants = detail?.variants?.length ? detail.variants : product ? [{ id: null, slug: "", name: product.name }] : [];
  const selectedVariant = variants.find((variant) => variant.slug === selectedVariantSlug) || variants[0] || null;
  const price = priceForVariant(detail?.prices || [], selectedVariant, countryIso2);
  const max = maxQuantity(selectedVariant);
  const image = primaryImage(product, selectedVariant);
  const requiresShipping = itemRequiresShipping(product, selectedVariant);

  useEffect(() => {
    if (!product?.slug) return;
    let active = true;
    setState({ loading: true, error: "" });
    apiFetch(`/api/v1/shop/products/${encodeURIComponent(product.slug)}?countryIso2=${encodeURIComponent(countryIso2)}`, {
      cache: "no-store",
      timeoutMs: 8000,
    })
      .then((data) => {
        if (!active) return;
        setDetail(data);
        setSelectedVariantSlug((current) => current || data.variants?.[0]?.slug || "");
      })
      .catch((error) => {
        if (active) setState({ loading: false, error: error.message || "No fue posible cargar el producto." });
      })
      .finally(() => {
        if (active) setState((current) => ({ ...current, loading: false }));
      });
    return () => {
      active = false;
    };
  }, [countryIso2, product?.slug]);

  useEffect(() => {
    setQuantity((current) => Math.min(Math.max(Number(current || 1), 1), max));
  }, [max, selectedVariantSlug]);

  const details = useMemo(() => [
    product?.requiresAuth ? "Requiere iniciar sesion para finalizar compra." : "Puedes agregarlo al carrito antes de iniciar sesion.",
    requiresShipping ? "Requiere direccion de envio." : "No requiere direccion de envio.",
    "El metodo de pago disponible se confirma en el carrito.",
  ], [product?.requiresAuth, requiresShipping]);

  if (!product) return null;

  return (
    <section className="nitro-section shop-product-detail">
      <nav className="shop-product-breadcrumb" aria-label="Ruta del producto">
        <a href="/tienda"><ArrowLeft size={15} /> Tienda</a>
        <span>/</span>
        <span>{product.name}</span>
      </nav>

      {state.error ? <Alert tone="danger">{state.error}</Alert> : null}

      <div className="shop-product-detail-grid">
        <div className="shop-product-gallery">
          {image ? <img alt={product.name} src={image} /> : <div className="shop-product-placeholder is-hero"><PackageCheck size={48} /></div>}
        </div>

        <article className="shop-product-info">
          <span className="nitro-chip">{typeLabel(product.type)}</span>
          <h2>{product.name}</h2>
          <p>{productSummary(product)}</p>
          {product.description && product.description !== product.subtitle ? <div className="shop-product-description">{product.description}</div> : null}
          <div className="shop-product-detail-badges">
            <span><Zap size={15} /> Entrega digital si aplica</span>
            <span><ShieldCheck size={15} /> Inventario validado</span>
            <span><Truck size={15} /> Envio segun producto</span>
          </div>
        </article>

        <aside className="shop-buy-panel">
          <div className="shop-buy-panel-head">
            <span>Precio</span>
            <strong>{price ? money(price.amount, countryIso2, price.currencyCode) : "Sin precio"}</strong>
          </div>

          {variants.length > 1 || selectedVariant?.slug ? (
            <label>
              <span>Variacion</span>
              <select value={selectedVariant?.slug || ""} onChange={(event) => setSelectedVariantSlug(event.target.value)}>
                {variants.map((variant) => <option key={variant.slug || "default"} value={variant.slug || ""}>{variant.name}</option>)}
              </select>
            </label>
          ) : null}

          <label>
            <span>Cantidad</span>
            <input min="1" max={max} type="number" value={quantity} onChange={(event) => setQuantity(Math.min(Math.max(Number(event.target.value || 1), 1), max))} />
          </label>

          <ul className="shop-buy-notes">
            {details.map((item) => <li key={item}>{item}</li>)}
          </ul>

          <div className="shop-drawer-payment">
            <CreditCard size={16} />
            <span>Pago seguro al finalizar</span>
          </div>

          <button className="nitro-primary-button shop-checkout-button" disabled={!price || state.loading} type="button" onClick={() => addProduct(product, selectedVariant, quantity)}>
            {state.loading ? <Loader2 size={16} /> : <ShoppingCart size={16} />}
            Agregar al carrito
          </button>
        </aside>
      </div>
    </section>
  );
}
